/* builder_steps.js — Modular builder UI (UNIVERSAL)
 * 
 * This file works with ANY product by reading from product metafields.
 * No product-specific code - all configuration comes from metafields.
 * 
 * CONTRACT:
 * - DOM IDs: #js-step-container, #js-next, #js-back, #js-validation-hint,
 *   #js-summary, #js-total, #js-add-to-cart, #js-steps-indicator, #js-notes
 * - JSON injections: #builder-rules, #builder-steps, #builder-sub-products, #builder-context
 * 
 * METAFIELDS (configure per product):
 * - custom.builder_rules: { core: { heights[], depths[] } }
 *   Supports: ["72","84"] or [{"value":"72","label":"72\""}]
 * 
 * - custom.builder_steps: { steps: [{
 *     title: "Step 1",
 *     required: true,
 *     sections: [{
 *       title: "CUSTOMIZE YOUR RACK",
 *       intro: "Text under section title",
 *       default_open: true,
 *       selectors: [{
 *         type: "height" | "depth",
 *         label: "RACK HEIGHT",
 *         intro: "Text before buttons",
 *         note: "Text after buttons (supports HTML)"
 *       }],
 *       products: [{
 *         handle: "product-handle",
 *         bind: ["Height", "Depth"],
 *         selection: "single"
 *       }]
 *     }]
 *   }]
 * }
 * 
 * - custom.sub_products: Product list metafield containing all sub-products
 * 
 * VARIANT BINDING:
 * - products[].bind: Array of option names like ["Height"], ["Depth"], or both
 * - Variant option names must match bind keys (case-insensitive)
 * - Each product binds independently when its required keys are selected
 * 
 * FEATURES:
 * - Supports unlimited steps, sections, selectors, products
 * - Custom intro/note text per selector via metafields
 * - Auto-binding of variants based on selections
 * - Manual ADD buttons for non-bound products
 */
(function () {
  const $ = (s, c=document)=>c.querySelector(s);
  const money = (cents, fmt)=> (fmt||'${{amount}}').replace('{{amount}}', ((cents||0)/100).toFixed(2));
  const strip = (html)=>html ? String(html).replace(/<[^>]+>/g,'') : '';

  // Loose numeric matching: extracts leading number for comparison
  // Handles variants like "72\" | 183cm" matching against "72"
  const leadNum = s => (String(s||'').match(/\d+/) || [])[0];
  const eqLoose = (a,b) => {
    const numA = leadNum(a), numB = leadNum(b);
    if(numA && numB) return numA === numB; // Both have numbers, compare them
    return String(a).toLowerCase() === String(b).toLowerCase(); // Fallback to exact match
  };

  function findVariant(prod, criteria){
    // Only match on criteria where value is truthy (no null/undefined/'')
    const validCriteria = Object.fromEntries(Object.entries(criteria).filter(([_,v])=>v));
    if(Object.keys(validCriteria).length === 0) return null; // No valid criteria to match
    
    // Compute names fresh from the product (important for multi-product scenarios)
    const names = (prod.options||[]).map(o=>(o.name||'').toLowerCase());
    const result = (prod.variants||[]).find(v=>{
      const m={};
      if(names[0]) m[names[0]]=v.option1||'';
      if(names[1]) m[names[1]]=v.option2||'';
      if(names[2]) m[names[2]]=v.option3||'';
      return Object.entries(validCriteria).every(([k,val]) => {
        const variantValue = m[k.toLowerCase()] || '';
        return eqLoose(variantValue, val);
      });
    });
    
    return result;
  }
  function renderPills(values, active, disabledSet, onClick, type, rules){
    const wrap=document.createElement('div');
    values.forEach(item=>{
      // Extract value and label from metafield (supports both string and object formats)
      const value = rules ? rules.getValue(item) : (typeof item === 'string' ? item : item?.value || item);
      const label = rules ? rules.getLabel(item) : (typeof item === 'string' ? item : item?.label || value);
      
      const el=document.createElement('button'); // Use button for better accessibility
      const isActive = String(value)===String(active);
      const isDisabled = disabledSet.has(String(value));
      el.className='pill'+(isActive?' active':'')+(isDisabled?' disabled':'');
      
      // Use label from metafield (or fall back to value)
      el.textContent = label;
      el.setAttribute('aria-label', `Select ${label} for ${type || 'option'}`);
      el.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      el.setAttribute('type', 'button'); // Prevent form submission
      
      if(!isDisabled) {
        // IMPORTANT: Pass original VALUE to onClick, not label
        // This ensures binding continues to work correctly with variant matching
        el.addEventListener('click',()=>onClick(value));
      }
      wrap.appendChild(el);
    });
    return wrap;
  }
  function renderColorSwatches(colors, activeKey, disabledSet, rules, onClick){
    const wrap=document.createElement('div'); wrap.className='swatches';
    colors.forEach(c=>{
      const key=(c.handle||c.label||c).toString().toLowerCase();
      const el=document.createElement('div');
      el.className='swatch'+(key===String(activeKey).toLowerCase()?' active':'')+(disabledSet.has(key)?' disabled':'');
      el.style.background = rules.swatchColor(key);
      if(!disabledSet.has(key)) el.addEventListener('click',()=>onClick(key));
      wrap.appendChild(el);
    });
    return wrap;
  }
  function variantPriceCents(v){ return v ? v.price : 0; }
  function idxByHandle(list){ const i={}; (list||[]).forEach(p=>i[p.handle]=p); return i; }

  // Fallback hydration: fetch full product if variants are missing
  async function ensureFullProduct(p) {
    if (p?.variants?.length) return p; // Already has variants
    if (!p?.handle) return p; // No handle to fetch
    
    try {
      const r = await fetch(`/products/${p.handle}.js`, {credentials:'same-origin'});
      if (!r.ok) return p;
      const full = await r.json();
      return Object.assign({}, p, full); // Merge to keep any existing properties
    } catch(e) {
      return p;
    }
  }

  document.addEventListener('DOMContentLoaded', async ()=>{
    const rulesJSON = JSON.parse($('#builder-rules')?.textContent||'{}');
    const stepsJSON = JSON.parse($('#builder-steps')?.textContent||'{}');
    const rawJson = $('#builder-sub-products')?.textContent || '[]';
    const rawSubProducts = JSON.parse(rawJson);
    const ctx = JSON.parse($('#builder-context')?.textContent||'{}');
    
    function collectConfiguredProductHandles(){
      const handles = [];
      const addHandle = (handle) => {
        if(handle && !handles.includes(handle)) handles.push(handle);
      };
      const addConfigHandles = (config) => {
        if(!config) return;
        addHandle(config.handle);
        (config.bundled_products || []).forEach(bundle => addHandle(bundle?.handle));
      };
      (stepsJSON.steps || []).forEach(step => {
        (step.sections || []).forEach(sec => {
          (sec.products || []).forEach(addConfigHandles);
          (sec.selectors || []).forEach(selector => {
            (selector.values || []).forEach(value => addConfigHandles(value?.product));
          });
        });
        (step.products || []).forEach(addConfigHandles);
        (step.selectors || []).forEach(selector => {
          (selector.values || []).forEach(value => addConfigHandles(value?.product));
        });
      });
      return handles;
    }

    const subProductSeed = Array.isArray(rawSubProducts) ? rawSubProducts.slice() : [];
    const seededHandles = new Set(subProductSeed.map(product => product?.handle).filter(Boolean));
    collectConfiguredProductHandles().forEach(handle => {
      if(!seededHandles.has(handle)){
        subProductSeed.push({ handle });
        seededHandles.add(handle);
      }
    });
    var subProducts = await Promise.all(subProductSeed.map(ensureFullProduct));
    
    const rules = new window.BuilderRules(rulesJSON);
    const productsByHandle = idxByHandle(subProducts);

    const elStepContainer = $('#js-step-container');
    const elNext = $('#js-next'), elBack=$('#js-back'), elHint=$('#js-validation-hint');
    const elSummary=$('#js-summary'), elTotal=$('#js-total'), elAdd=$('#js-add-to-cart'), elIndicator=$('#js-steps-indicator'), elNotes=$('#js-notes');
    const elSummaryTab = $('#js-summary-tab'), elSummaryContent = $('#js-summary-content'), elSummaryPlus = $('#js-summary-plus');
    const elReset = $('#js-reset'), elStepLabel = $('#js-step-label');

    const state={ stepIndex:0, selections:{}, selectionDetails:{}, lineItems:{}, openSections:[] };
    const steps=(stepsJSON.steps||[]);
    if(!steps.length){ elStepContainer.innerHTML='<p>Builder not configured.</p>'; return; }

    function updateIndicator(){
      // Update step label (STEP 1, STEP 2, etc.)
      if(elStepLabel) elStepLabel.textContent = `STEP ${state.stepIndex + 1}`;
      
      // Update step indicator - generate steps dynamically
      if(elIndicator){
        const totalSteps = steps.length;
        elIndicator.innerHTML = steps.map((s, i) => {
          const isActive = i === state.stepIndex;
          const separator = i < totalSteps - 1 ? '<span class="step-separator">—</span>' : '';
          return `<span class="step-item ${isActive ? 'active' : ''}" data-step="${i + 1}">Step ${i + 1}</span>${separator}`;
        }).join('');
      }
    }
    function setHint(m){ if(!m){elHint.style.display='none';elHint.textContent='';} else {elHint.style.display='block';elHint.textContent=m;} }

    // ---------- ACCORDION UI ----------
    function accItem(title, open=false){
      const item=document.createElement('div'); item.className='acc-item'+(open?' open':'');
      const hd=document.createElement('div'); hd.className='acc-hd';
      const h3=document.createElement('h3'); h3.textContent=title||'';
      const ico=document.createElement('span'); ico.className='acc-ico'; ico.innerHTML='&#9650;';
      hd.appendChild(h3); hd.appendChild(ico);
      const bd=document.createElement('div'); bd.className='acc-bd';
      hd.addEventListener('click',(e)=>{
        e.stopPropagation(); // Prevent any parent click handlers
        item.classList.toggle('open');
        // Also toggle body display
        if(item.classList.contains('open')){
          bd.style.display = 'block';
        } else {
          bd.style.display = 'none';
        }
      });
      item.appendChild(hd); item.appendChild(bd);
      return {item, body:bd};
    }

    // ---------- RENDER SELECTOR BLOCK ----------
    function selectorKey(sel){
      if(sel.key) return sel.key;
      if(sel.type==='color') return 'Color';
      if(sel.type==='height') return 'Height';
      if(sel.type==='depth') return 'Depth';
      return sel.label || sel.type || 'Option';
    }

    function setSelection(sel, value, item){
      const key = selectorKey(sel);
      state.selections[key] = value;
      state.selectionDetails[key] = {
        value: value,
        label: item?.label || String(value || ''),
        product: item?.product || null
      };
    }

    function removeSelectorLineItems(key){
      const prefix = `selector:${key}__`;
      Object.keys(state.lineItems).forEach(lineKey => {
        if(lineKey.startsWith(prefix)) delete state.lineItems[lineKey];
      });
    }

    function applySelectorProduct(sel, item){
      const key = selectorKey(sel);
      removeSelectorLineItems(key);
      if(!item?.product) return;
      const config = item.product;
      const prod = productsByHandle[config.handle];
      if(!prod) return;
      const variant = resolveConfiguredVariant(config, prod);
      if(!variant || variant.available===false) return;
      const lineKey = `selector:${key}__${config.handle}__variant:${variant.id}`;
      state.lineItems[lineKey] = {
        variantId: variant.id,
        qty: config.quantity || 1,
        title: config.title || item.label || prod.title,
        priceCents: variantPriceCents(variant),
        sectionTitle: sel.label || key,
        bundledProducts: config.bundled_products || []
      };
    }

    function renderSelectors(container, selectors){
      (selectors||[]).forEach(sel=>{
        // Selector title
        const lab=document.createElement('div'); lab.style.margin='14px 0 6px'; lab.style.fontWeight='800'; lab.textContent=sel.label||sel.type; container.appendChild(lab);
        
        // Intro text BEFORE buttons (e.g., description under title)
        if(sel.intro){ 
          const intro=document.createElement('div'); 
          intro.className='note'; 
          intro.style.marginBottom='12px';
          intro.style.lineHeight='1.6';
          intro.innerHTML=sel.intro; 
          container.appendChild(intro); 
        }

        if(Array.isArray(sel.values) && sel.values.length){
          const key = selectorKey(sel);
          const values = sel.values;
          const active = state.selections[key];
          container.appendChild(renderPills(values, active, new Set(), (v)=>{
            const selected = values.find(item => String((typeof item === 'string' ? item : item?.value)) === String(v));
            setSelection(sel, v, selected);
            applySelectorProduct(sel, selected);
            cleanupHiddenConditionalLineItems();
            refreshSummary();
            updateValidationHint();
            renderStep(state.stepIndex);
          }, sel.type || key, {
            getValue: (item) => typeof item === 'string' ? item : item?.value,
            getLabel: (item) => typeof item === 'string' ? item : (item?.label || item?.value)
          }));
        } else if(sel.type==='color'){
          const colors = rules.allColors();
          const disabled = new Set(colors.filter(c=>c.available===false).map(c=>(c.handle||c.label||'').toLowerCase()));
          let active = state.selections.Color || rules.defaultColor() || (colors[0]?.handle);
          if(!state.selections.Color) state.selections.Color = active;
          if(!state.selectionDetails.Color) state.selectionDetails.Color = { value: active, label: active };
          container.appendChild(
            renderColorSwatches(colors, state.selections.Color, disabled, rules, (key)=>{
              state.selections.Color = key;
              state.selectionDetails.Color = { value: key, label: key };
              if(!rules.heightsForColor(key).includes(state.selections.Height)) state.selections.Height=null;
              if(!rules.depthsForColor(key).includes(state.selections.Depth))  state.selections.Depth=null;
              renderStep(state.stepIndex); refreshSummary();
            })
          );
        }
        if(sel.type==='height'){
          const vals = rules.coreHeights() || [];
          const active = state.selections.Height;
          if(vals.length > 0) {
            container.appendChild(renderPills(vals, active, new Set(), (v)=>{
              state.selections.Height=v;
              state.selectionDetails.Height = { value: v, label: String(v) };
              resolveBoundVariants();
              refreshSummary(); // Update summary and button states
              updateValidationHint(); // Update hint message
              renderStep(state.stepIndex); // Re-render to update visual states (after other updates)
            }, 'height', rules)); // Pass rules for label extraction
          }
        }
        if(sel.type==='depth'){
          const vals = rules.coreDepths() || [];
          const active = state.selections.Depth;
          if(vals.length > 0) {
            container.appendChild(renderPills(vals, active, new Set(), (v)=>{
              state.selections.Depth=v;
              state.selectionDetails.Depth = { value: v, label: String(v) };
              resolveBoundVariants();
              refreshSummary(); // Update summary and button states
              updateValidationHint(); // Update hint message
              renderStep(state.stepIndex); // Re-render to update visual states (after other updates)
            }, 'depth', rules)); // Pass rules for label extraction
          }
        }
        
        // Note text AFTER buttons (e.g., shipping info, warnings)
        if(sel.note){ 
          const note=document.createElement('div'); 
          note.className='note'; 
          note.style.marginTop='12px';
          note.style.lineHeight='1.6';
          note.innerHTML=sel.note; 
          container.appendChild(note); 
        }
      });
    }

    // ---------- HELPER: Find section title for a product handle ----------
    function findSectionTitleForProduct(handle){
      // Search through all steps and sections to find which section contains this product
      for(const step of steps){
        if(step.sections){
          for(const section of step.sections){
            if(section.products){
              const found = section.products.find(p => p.handle === handle);
              if(found) return section.title || '';
            }
          }
        }
      }
      return '';
    }
    
    // ---------- HELPER: Generate unique key for lineItems (section + handle + variant) ----------
    function getLineItemKey(sectionTitle, handle, variantId){
      // Use section title + handle + variantId to make selections independent
      // This allows same handle with different SKUs to be selected separately
      const variantKey = variantId ? `__variant:${variantId}` : '';
      return `${sectionTitle || ''}__${handle || ''}${variantKey}`;
    }
    
    // ---------- HELPER: Check if product is selected in a specific section ----------
    function isProductSelectedInSection(sectionTitle, handle, variantId){
      const key = getLineItemKey(sectionTitle, handle, variantId);
      return !!state.lineItems[key];
    }

    function optionValueMatches(allowed, selectedValue, selectedDetail){
      if(selectedValue == null) return false;
      const allowedText = String(allowed || '');
      const selectedText = String(selectedValue || '');
      if(allowedText === selectedText) return true;
      const selectedLabel = String(selectedDetail?.label || '');
      if(selectedLabel && allowedText === selectedLabel) return true;

      const allowedProduct = allowedText.split(':')[0];
      const selectedProduct = selectedText.split(':')[0];
      if(allowedProduct && selectedProduct && allowedProduct === selectedProduct) {
        const allowedNum = leadNum(allowedText.split(':').slice(1).join(':') || allowedText);
        const selectedNum = leadNum(selectedLabel || selectedText);
        if(allowedNum && selectedNum && allowedNum === selectedNum) return true;
      }
      return false;
    }

    function conditionMatches(config){
      if(!config || !config.if_selected) return true;
      return Object.entries(config.if_selected).every(([key, allowedValues]) => {
        const selectedValue = state.selections[key];
        const selectedDetail = state.selectionDetails[key];
        if(!selectedValue) return false;
        const allowedArray = Array.isArray(allowedValues) ? allowedValues : [allowedValues];
        return allowedArray.some(allowed => optionValueMatches(allowed, selectedValue, selectedDetail));
      });
    }

    function resolveConfiguredVariant(config, prod){
      if(!prod) return null;
      let variant = null;
      if(config.variant_id){
        variant = (prod.variants||[]).find(item => String(item.id) === String(config.variant_id));
      }
      if(!variant && config.sku){
        variant = (prod.variants||[]).find(item => item.sku === config.sku);
      }
      if(!variant && config.bind && config.bind.length > 0){
        const crit={}; (config.bind||[]).forEach(b=>crit[b]=state.selections[b]);
        variant = findVariant(prod, crit);
      }
      if(!variant && config.default_variant){
        const variants = prod.variants || [];
        if(config.default_variant === 'first_available'){
          variant = variants.find(item => item.available !== false) || variants[0];
        } else if(config.default_variant === 'first'){
          variant = variants[0];
        } else if(config.default_variant === 'cheapest'){
          variant = variants.reduce((cheapest, current) => {
            if(!cheapest) return current;
            return (current.price || 0) < (cheapest.price || 0) ? current : cheapest;
          }, null);
        }
      }
      return variant || (prod.variants||[])[0] || null;
    }

    function cleanupHiddenConditionalLineItems(){
      (steps || []).forEach(step => {
        (step.sections || []).forEach(section => {
          (section.products || []).forEach(config => {
            if(!config.if_selected || conditionMatches(config)) return;
            const prod = productsByHandle[config.handle];
            const variant = resolveConfiguredVariant(config, prod);
            if(!variant) return;
            delete state.lineItems[getLineItemKey(section.title || '', config.handle, variant.id)];
          });
        });
      });
    }

    // ---------- RENDER PRODUCTS (cards or buttons) ----------
    function productCardGrid(container, products, sectionProducts, sectionTitle){
      const grid=document.createElement('div'); grid.className='card-grid';
      (products||[]).forEach((p, pIndex)=>{
        const prod=productsByHandle[p.handle]; if(!prod) return;
        const card=document.createElement('div'); card.className='card';
        
        // Check conditional display (e.g., based on rack height)
        if(!conditionMatches(p)){
          const staleVariant = resolveConfiguredVariant(p, prod);
          if(staleVariant){
            delete state.lineItems[getLineItemKey(sectionTitle, prod.handle, staleVariant.id)];
          }
          return;
        }
        
        // Resolve variant first to get variantId for key generation
        let v = resolveConfiguredVariant(p, prod);
        
        // Check if this product is currently selected in THIS section
        const isSelected = isProductSelectedInSection(sectionTitle, prod.handle, v ? v.id : null);
        if(isSelected) card.classList.add('card-selected');

        // image (fixed size, won't scale)
        // Priority: variant image > product image
        let imgSrc = null;
        
        // First, try to get variant image if variant exists
        if(v){
          // Check variant.image (Shopify Product API format)
          if(v.image){
            imgSrc = typeof v.image === 'string' ? v.image : (v.image.src || v.image);
          }
          // Check variant.featured_image
          if(!imgSrc && v.featured_image){
            imgSrc = typeof v.featured_image === 'string' ? v.featured_image : (v.featured_image.src || v.featured_image);
          }
          // Check if variant has image_id, then find it in product.images
          if(!imgSrc && v.image_id && prod?.images){
            const variantImage = prod.images.find(img => {
              const imgId = typeof img === 'object' ? (img.id || img.media_id) : null;
              return imgId === v.image_id;
            });
            if(variantImage){
              imgSrc = typeof variantImage === 'string' ? variantImage : (variantImage.src || variantImage);
            }
          }
        }
        
        // Fallback to product image if variant has no image
        if(!imgSrc && prod?.featured_image){
          imgSrc = typeof prod.featured_image === 'string' ? prod.featured_image : (prod.featured_image.src || prod.featured_image);
        }
        if(!imgSrc && prod?.images && prod.images.length > 0){
          const firstImg = prod.images[0];
          imgSrc = typeof firstImg === 'string' ? firstImg : (firstImg.src || firstImg);
        }
        if(!imgSrc && prod?.media && prod.media.length > 0){
          const firstMedia = prod.media[0];
          if(firstMedia.preview_image){
            imgSrc = typeof firstMedia.preview_image === 'string' ? firstMedia.preview_image : (firstMedia.preview_image.src || firstMedia.preview_image);
          } else if(firstMedia.src){
            imgSrc = typeof firstMedia.src === 'string' ? firstMedia.src : firstMedia.src;
          }
        }
        
        if(imgSrc){ 
          const imgWrap = document.createElement('div'); 
          imgWrap.className='card-img-wrap';
          const img=document.createElement('img'); 
          img.src=imgSrc; 
          img.alt=strip(prod.title);
          img.onerror = () => {
            imgWrap.style.display = 'none'; // Hide if image fails to load
          };
          imgWrap.appendChild(img);
          card.appendChild(imgWrap); 
        } else {
          // No image available for product - create empty placeholder to maintain card structure
          const imgWrap = document.createElement('div'); 
          imgWrap.className='card-img-wrap';
          imgWrap.style.background='#f9fafb';
          card.appendChild(imgWrap);
        }

        const body=document.createElement('div'); body.className='card-bd';
        const ttl=document.createElement('div'); ttl.className='card-ttl';
        // Use custom title if provided, otherwise use product title
        const displayTitle = p.title || prod.title;
        // Add info icon for all products - click to go to product page
        const infoIcon = document.createElement('span');
        infoIcon.className = 'info-ico';
        infoIcon.textContent = 'i';
        infoIcon.title = 'View product details';
        infoIcon.style.cursor = 'pointer';
        infoIcon.addEventListener('click', (e) => {
          e.stopPropagation();
          window.location.href = `/products/${prod.handle}`;
        });
        ttl.textContent = strip(displayTitle);
        ttl.appendChild(infoIcon);
        body.appendChild(ttl);

        // v is already resolved above
        const row=document.createElement('div'); row.className='card-row';
        const price=document.createElement('div'); 
        price.className='card-price';
        price.textContent = v ? money(variantPriceCents(v), ctx.money_format) : 'Unavailable';
        row.appendChild(price);
        
        // Status indicator (SELECTED or + ADD button)
        if(isSelected){
          // Get existing line item to show quantity
          const existingKey = getLineItemKey(sectionTitle, prod.handle, v ? v.id : null);
          const existingItem = state.lineItems[existingKey];
          const currentQty = existingItem ? existingItem.qty : 1;
          
          // Check if product supports quantity selection
          // Only auto-bound products (pairs) with quantity > 1 support quantity controls
          // All other products (including qty selection) show checkmark/SELECTED indicator
          const supportsQuantity = p.quantity === true || (p.bind && p.bind.length > 0 && p.quantity !== false && (p.quantity || 1) > 1);
          
          if(supportsQuantity){
            // Show quantity controls (only for auto-bound products with quantity > 1)
            const qtyWrap = document.createElement('div');
            qtyWrap.className='card-qty-controls';
            const minusBtn = document.createElement('button');
            minusBtn.textContent = '-';
            minusBtn.className = 'qty-btn';
            minusBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              const key = getLineItemKey(sectionTitle, prod.handle, v.id);
              const item = state.lineItems[key];
              if(item && item.qty > 1){
                item.qty--;
                refreshSummary();
                renderStep(state.stepIndex);
              } else if(item && item.qty === 1){
                // Remove item if qty becomes 0
                delete state.lineItems[key];
                refreshSummary();
                updateValidationHint();
                renderStep(state.stepIndex);
              }
            });
            const qtyDisplay = document.createElement('span');
            qtyDisplay.className = 'qty-display';
            qtyDisplay.textContent = currentQty;
            const plusBtn = document.createElement('button');
            plusBtn.textContent = '+';
            plusBtn.className = 'qty-btn';
            plusBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              const key = getLineItemKey(sectionTitle, prod.handle, v.id);
              const item = state.lineItems[key];
              if(item){
                item.qty++;
                refreshSummary();
                renderStep(state.stepIndex);
              }
            });
            qtyWrap.appendChild(minusBtn);
            qtyWrap.appendChild(qtyDisplay);
            qtyWrap.appendChild(plusBtn);
            row.appendChild(qtyWrap);
          } else {
            // Show checkmark/SELECTED indicator for all other products (single, qty, etc.)
            const selectedWrap = document.createElement('div');
            selectedWrap.className='card-selected-indicator';
            const checkmark = document.createElement('span');
            checkmark.className='card-checkmark';
            checkmark.innerHTML='✓';
            const selectedText = document.createElement('span');
            selectedText.className='card-selected-text';
            selectedText.textContent='SELECTED';
            selectedWrap.appendChild(checkmark);
            selectedWrap.appendChild(selectedText);
            row.appendChild(selectedWrap);
            
            // Make selected card clickable to deselect (for both single and qty selection products)
            if(p.selection === 'single' || p.selection === 'qty'){
              card.classList.add('card-clickable');
              card.addEventListener('click', (e) => {
                if(e.target.closest('.info-ico')) return;
                const key = getLineItemKey(sectionTitle, prod.handle, v.id);
                delete state.lineItems[key];
                refreshSummary();
                updateValidationHint();
                renderStep(state.stepIndex);
              });
            }
          }
        } else {
          // Make entire card clickable to add
          card.style.cursor = 'pointer';
          card.addEventListener('click', (e) => {
            if(e.target.closest('.info-ico')) return;
            if(!v || v.available===false) return;
            
            // For single selection, remove other products from the same section first
            if(p.selection === 'single' && sectionProducts){
              sectionProducts.forEach((otherP, otherPIndex) => {
                if(otherPIndex !== pIndex){ // Skip self based on array index, not handle
                  // Need to resolve otherP variant to get correct key
                  const otherProd = productsByHandle[otherP.handle];
                  if(!otherProd) return;
                  // Quick variant resolution for otherP
                  let otherV = null;
                  if(otherP.variant_id) otherV = (otherProd.variants||[]).find(variant => variant.id === otherP.variant_id);
                  else if(otherP.sku) otherV = (otherProd.variants||[]).find(variant => variant.sku === otherP.sku);
                  else otherV = (otherProd.variants||[])[0];
                  
                  if(otherV){
                    const otherKey = getLineItemKey(sectionTitle, otherP.handle, otherV.id);
                    if(state.lineItems[otherKey]){
                      delete state.lineItems[otherKey];
                    }
                  }
                }
              });
            }
            
            const key = getLineItemKey(sectionTitle, prod.handle, v.id);
            const displayTitle = p.title || prod.title; // Use custom title if provided
            state.lineItems[key]={ variantId:v.id, qty:1, title:displayTitle, priceCents:variantPriceCents(v), sectionTitle: sectionTitle, bundledProducts: p.bundled_products || [] };
            refreshSummary();
            updateValidationHint();
            renderStep(state.stepIndex);
          });
          
          // Also add button for visual clarity
          const btn=document.createElement('button'); 
          btn.className='btn-mini'; 
          btn.textContent='+ ADD';
          btn.disabled = !v || (v.available===false);
          btn.addEventListener('click',(e)=>{
            e.stopPropagation();
            if(!v || v.available===false) return;
            
            if(p.selection === 'single' && sectionProducts){
              sectionProducts.forEach((otherP, otherPIndex) => {
                if(otherPIndex !== pIndex){ // Skip self based on array index, not handle
                  const otherProd = productsByHandle[otherP.handle];
                  if(!otherProd) return;
                  let otherV = null;
                  if(otherP.variant_id) otherV = (otherProd.variants||[]).find(variant => variant.id === otherP.variant_id);
                  else if(otherP.sku) otherV = (otherProd.variants||[]).find(variant => variant.sku === otherP.sku);
                  else otherV = (otherProd.variants||[])[0];
                  
                  if(otherV){
                    const otherKey = getLineItemKey(sectionTitle, otherP.handle, otherV.id);
                    if(state.lineItems[otherKey]){
                      delete state.lineItems[otherKey];
                    }
                  }
                }
              });
            }
            
            const key = getLineItemKey(sectionTitle, prod.handle, v.id);
            const displayTitle = p.title || prod.title; // Use custom title if provided
            state.lineItems[key]={ variantId:v.id, qty:1, title:displayTitle, priceCents:variantPriceCents(v), sectionTitle: sectionTitle, bundledProducts: p.bundled_products || [] };
            refreshSummary();
            updateValidationHint();
            renderStep(state.stepIndex);
          });
          row.appendChild(btn);
        }
        
        body.appendChild(row);

        card.appendChild(body); grid.appendChild(card);
      });
      container.appendChild(grid);
    }

    // ---------- ACCORDION STATE MANAGEMENT ----------
    // Save accordion open/closed state before re-render
    function saveAccordionState(){
      const openSections = [];
      const accItems = elStepContainer.querySelectorAll('.acc-item');
      accItems.forEach(item => {
        if(item.classList.contains('open')){
          const titleEl = item.querySelector('.acc-hd h3');
          if(titleEl) openSections.push(titleEl.textContent.trim());
        }
      });
      state.openSections = openSections;
    }
    
    // Restore accordion state after re-render
    function restoreAccordionState(){
      if(!state.openSections || state.openSections.length === 0) return;
      const accItems = elStepContainer.querySelectorAll('.acc-item');
      accItems.forEach(item => {
        const titleEl = item.querySelector('.acc-hd h3');
        if(titleEl && state.openSections.includes(titleEl.textContent.trim())){
          item.classList.add('open');
          const body = item.querySelector('.acc-bd');
          if(body) body.style.display = 'block';
        }
      });
    }

    // ---------- MAIN RENDER ----------
    function renderStep(i){
      // Save accordion state before clearing
      saveAccordionState();
      
      state.stepIndex=i; cleanupHiddenConditionalLineItems(); updateIndicator(); setHint(''); elStepContainer.innerHTML='';
      const step=steps[i];
      const host=document.createElement('div');

      // 顶部 headline/copy（可选）
      if(step.headline){ const h=document.createElement('div'); h.style.fontWeight='900'; h.style.fontSize='22px'; h.style.textTransform='uppercase'; h.style.margin='4px 0 10px'; h.textContent=step.headline; host.appendChild(h); }
      if(step.copy){ const p=document.createElement('div'); p.className='note'; p.innerHTML=step.copy; host.appendChild(p); }

      if(Array.isArray(step.sections) && step.sections.length){
        const acc=document.createElement('div'); acc.className='acc';
        step.sections.forEach((sec, idx)=>{
          const {item, body} = accItem(sec.title || `Section ${idx+1}`, !!sec.default_open);
          // section intro（标题下方）
          if(sec.intro){ const si=document.createElement('div'); si.className='note'; si.style.margin='0 0 10px'; si.innerHTML=sec.intro; body.appendChild(si); }
          // selectors（若有）
          if(sec.selectors && sec.selectors.length){ renderSelectors(body, sec.selectors); }
          // products
          if(sec.products && sec.products.length){
            // Auto-enable cards mode for all sections except "CUSTOMIZE YOUR RACK"
            // Cards mode shows products with images, names, prices, and selection state
            const useCards = sec.cards !== false && sec.title !== 'CUSTOMIZE YOUR RACK';
            
            if(useCards) {
              // Filter out auto-bound products (they don't need manual selection UI)
              const manualProducts = sec.products.filter(p => !p.bind || p.bind.length === 0);
              if(manualProducts.length > 0) {
                productCardGrid(body, manualProducts, sec.products, sec.title);
              }
            } else {
              // Only show manual ADD buttons for products without auto-binding
              // Products with bind[] are automatically added when selections are made
              sec.products.forEach(p=>{
                // Skip products that have auto-binding (they're handled by resolveBoundVariants)
                if(p.bind && p.bind.length > 0) return;
                
                const prod=productsByHandle[p.handle]; if(!prod) return;
                const row=document.createElement('div'); row.style.marginTop='12px';
                const h=document.createElement('div'); h.style.fontWeight='600'; h.textContent=prod.title; row.appendChild(h);
                const crit={}; (p.bind||[]).forEach(b=>crit[b]=state.selections[b]);
                const v=findVariant(prod, crit) || (prod.variants||[])[0];
                const btn=document.createElement('button'); btn.className='btn btn-secondary';
                btn.textContent = v ? `Add – ${money(variantPriceCents(v), ctx.money_format)}` : 'Unavailable';
                btn.disabled = !v || (v.available===false);
                btn.addEventListener('click',()=>{
                  if(!v) return;
                  
                  // For single selection, remove other products from the same section first
                  if(p.selection === 'single' && sec.products){
                    sec.products.forEach(otherP => {
                      if(otherP.handle !== prod.handle && state.lineItems[otherP.handle]){
                        delete state.lineItems[otherP.handle];
                      }
                    });
                  }
                  
                  state.lineItems[prod.handle]={ variantId:v.id, qty:1, title:prod.title, priceCents:variantPriceCents(v), bundledProducts: p.bundled_products || [] };
                  refreshSummary();
                  updateValidationHint(); // Update hint message
                  renderStep(state.stepIndex); // Re-render to update visual states
                });
                row.appendChild(btn);
                body.appendChild(row);
              });
            }
          }
          acc.appendChild(item);
        });
        host.appendChild(acc);
      } else {
        // 兼容老结构：selectors + products
        renderSelectors(host, step.selectors || []);
        (step.products||[]).forEach(p=>{
          // Skip products that have auto-binding (they're handled by resolveBoundVariants)
          if(p.bind && p.bind.length > 0) return;
          
          const prod = productsByHandle[p.handle]; if(!prod) return;
          const row=document.createElement('div'); row.style.marginTop='12px';
          const h=document.createElement('div'); h.style.fontWeight='600'; h.textContent=prod.title; row.appendChild(h);
          const crit={}; (p.bind||[]).forEach(b=>crit[b]=state.selections[b]);
          const v=findVariant(prod, crit) || (prod.variants||[])[0];
          const btn=document.createElement('button'); btn.className='btn btn-secondary';
          btn.textContent = v ? `Add – ${money(variantPriceCents(v), ctx.money_format)}` : 'Unavailable';
          btn.disabled = !v || (v.available===false);
          btn.addEventListener('click',()=>{
            if(!v) return;
            // For old structure, use handle as key (no section)
            const key = prod.handle;
            state.lineItems[key]={ variantId:v.id, qty:1, title:prod.title, priceCents:variantPriceCents(v), bundledProducts: p.bundled_products || [] };
            refreshSummary();
            updateValidationHint(); // Update hint message
          });
          row.appendChild(btn);
          host.appendChild(row);
        });
      }

      elStepContainer.appendChild(host);
      
      // Restore accordion state after rendering
      restoreAccordionState();
      
      elBack.disabled=(i===0);
      updateNextButtonState(); // Use centralized validation
      // Only show add to cart button on the last step
      const addToCartWrapper = document.querySelector('#js-add-to-cart-wrapper');
      if(elAdd && addToCartWrapper){
        if(i === steps.length - 1){
          addToCartWrapper.style.display = 'block';
          elAdd.disabled = !validateAll();
        } else {
          addToCartWrapper.style.display = 'none';
        }
      }
    }

    function validateStep(i){
      const step=steps[i]; let ok=true; setHint('');
      // 收集所有 selectors / products（兼容两种结构）
      const selectors = (step.sections||[]).flatMap(s=>s.selectors||[]).concat(step.selectors||[]);
      const products  = (step.sections||[]).flatMap(s=>s.products ||[]).concat(step.products ||[]);

      if(step.required){
        // Check selectors
        selectors.forEach(sel=>{
          const key = selectorKey(sel);
          if(!state.selections[key]) ok=false;
        });
        
        // For sections structure, check that every section (except CUSTOMIZE YOUR RACK) has at least one product selected
        // This is the main validation - each section must have at least one product selected
        if(step.sections && step.sections.length > 0){
          step.sections.forEach(section => {
            if(section.required === false) return;
            // Skip "CUSTOMIZE YOUR RACK" section (it has selectors, not products)
            if(section.title === 'CUSTOMIZE YOUR RACK' || section.selectors) return;
            
            // Every other section with products must have at least one product selected
            const visibleProducts = (section.products || []).filter(p => conditionMatches(p));
            const hasProducts = visibleProducts.length > 0;
            const hasManualProducts = hasProducts && visibleProducts.some(p => !p.bind || p.bind.length === 0);
            
            // Only validate sections that have manual-selectable products
            if(hasManualProducts && !hasSectionProductSelected(section)){
              ok = false;
            }
          });
        } else {
          // Fallback: Check products in old structure (no sections)
          products.forEach(p=>{
            // Skip products that auto-bind (they're handled automatically)
            if(p.bind && p.bind.length > 0) return;
            if(p.selection==='single' && !state.lineItems[p.handle]) ok=false;
            if(p.selection==='qty'){
              const li=state.lineItems[p.handle];
              if(!li || (p.min_qty && (!li.qty || li.qty<p.min_qty))) ok=false;
            }
          });
        }
      }
      // Don't set hint here - it will be updated by updateValidationHint()
      return ok;
    }
    function validateAll(){ return (steps||[]).every((_,i)=>validateStep(i)); }

    function resolveBoundVariants(){
      // 自动绑定第一步（通常是核心机身）
      // Each product binds independently when its required keys are ready
      const first=steps[0]||{};
      const products = (first.sections||[]).flatMap(s=>s.products||[]).concat(first.products||[]);
      products.forEach(p=>{
        if(!p.bind||!p.bind.length) return;
        const prod=productsByHandle[p.handle]; if(!prod) return;
        
        // Check if THIS product's required bind keys are ready
        // Each product binds independently when its required keys are selected
        const ready = (p.bind || []).every(key => !!state.selections[key]);
        
        // Find section title for this auto-bound product
        // Auto-bound products are usually in "CUSTOMIZE YOUR RACK" section
        let sectionTitleForAutoBound = findSectionTitleForProduct(p.handle) || '';
        
        // If not found, try to find from first step's sections
        if(!sectionTitleForAutoBound && steps[0]?.sections){
          for(const sec of steps[0].sections){
            if(sec.products && sec.products.some(sp => sp.handle === p.handle)){
              sectionTitleForAutoBound = sec.title || '';
              break;
            }
          }
        }
        
        // Build criteria using only the keys this product needs
        const crit={}; 
        (p.bind||[]).forEach(b=>{
          if(state.selections[b]) crit[b]=state.selections[b];
        });
        
        // Find variant FIRST to determine the key
        const v=findVariant(prod, crit);
        
        // Generate key with variantId
        const keyForAutoBound = getLineItemKey(sectionTitleForAutoBound, p.handle, v ? v.id : null);
        
        if(!ready) {
          // Need to delete all variants of this product
          const allKeys = Object.keys(state.lineItems);
          allKeys.forEach(key => {
            if(key.startsWith(`${sectionTitleForAutoBound}__${p.handle}`)){
              delete state.lineItems[key];
            }
          });
          return;
        }
        
        // Delete any OTHER variants of this product (in case selection changed)
        const allKeys = Object.keys(state.lineItems);
        allKeys.forEach(key => {
          if(key.startsWith(`${sectionTitleForAutoBound}__${p.handle}`) && key !== keyForAutoBound){
            delete state.lineItems[key];
          }
        });
        
        if(v){ 
          state.lineItems[keyForAutoBound]={ 
            variantId:v.id, 
            qty:p.quantity || 1, // Support quantity from config (default to 2 for pairs)
            title:prod.title, 
            priceCents:variantPriceCents(v),
            sectionTitle: sectionTitleForAutoBound,
            bundledProducts: p.bundled_products || []
          };
        }
        else { 
          delete state.lineItems[keyForAutoBound];
        }
      });
    }

    function syncBundledLineItems(){
      const parentEntries = Object.entries(state.lineItems).filter(([_, item]) => !item.bundleParentKey);
      Object.keys(state.lineItems).forEach(key => {
        if(state.lineItems[key]?.bundleParentKey) delete state.lineItems[key];
      });

      parentEntries.forEach(([parentKey, parent]) => {
        (parent.bundledProducts || []).forEach(bundle => {
          if(!bundle?.handle) return;
          const prod = productsByHandle[bundle.handle];
          if(!prod) return;
          const variant = resolveConfiguredVariant(bundle, prod);
          if(!variant || variant.available===false) return;
          const bundleKey = `bundle:${parentKey}__${bundle.handle}__variant:${variant.id}`;
          state.lineItems[bundleKey] = {
            variantId: variant.id,
            qty: bundle.quantity || 1,
            title: bundle.title || prod.title,
            priceCents: variantPriceCents(variant),
            sectionTitle: parent.sectionTitle || '',
            bundleParentKey: parentKey,
            bundleParentTitle: parent.title || '',
            bundledProducts: []
          };
        });
      });
    }

    function refreshSummary(){
      syncBundledLineItems();
      elSummary.innerHTML=''; let total=0;
      Object.values(state.lineItems).forEach(li=>{
        const row=document.createElement('li'); row.className='summary-row';
        row.innerHTML=`<span>${li.title} × ${li.qty}</span><span>${money(li.priceCents*li.qty, ctx.money_format)}</span>`;
        elSummary.appendChild(row); total += li.priceCents*li.qty;
      });
      elTotal.textContent = money(total, ctx.money_format);
      
      // Update summary tab clickability - only clickable when there are items
      const hasItems = Object.keys(state.lineItems).length > 0;
      if(hasItems) {
        elSummaryTab.style.cursor = 'pointer';
        elSummaryTab.style.pointerEvents = 'auto';
      } else {
        elSummaryTab.style.cursor = 'default';
        elSummaryTab.style.opacity = '0.6';
      }
      
      // Update Next button with validation
      updateNextButtonState();
      elAdd.disabled=(state.stepIndex!==steps.length-1) || !validateAll();
    }
    
    // Helper: Check if any product from a section is selected
    function hasSectionProductSelected(section) {
      const sectionProducts = (section.products || []).filter(p => conditionMatches(p));
      if(sectionProducts.length === 0) return true;
      const sectionTitle = section.title || '';
      // Check if any product from this section (excluding auto-bound) is in lineItems
      return sectionProducts.some(p => {
        // Skip auto-bound products (they're handled separately)
        if(p.bind && p.bind.length > 0) return false;
        
        // Need to resolve variant for proper checking
        const prod = productsByHandle[p.handle];
        if(!prod) return false;
        
        // Quick variant resolution
        let v = null;
        if(p.variant_id) v = (prod.variants||[]).find(variant => variant.id === p.variant_id);
        else if(p.sku) v = (prod.variants||[]).find(variant => variant.sku === p.sku);
        else if(p.bind && p.bind.length > 0){
          const crit={}; (p.bind||[]).forEach(b=>crit[b]=state.selections[b]);
          v = findVariant(prod, crit);
        }
        else v = (prod.variants||[])[0];
        
        // Check if this product is in lineItems for this section
        return isProductSelectedInSection(sectionTitle, p.handle, v ? v.id : null);
      });
    }
    
    // Get list of missing required items with proper display names
    function getMissingRequirements(){
      const step=steps[state.stepIndex];
      const missing = [];
      
      if(step.required){
        // Handle sections structure
        if(step.sections && step.sections.length > 0){
          step.sections.forEach(section => {
            if(section.required === false) return;
            // For "CUSTOMIZE YOUR RACK" section ONLY, use selector LABELS (RACK HEIGHT, RACK DEPTH)
            // This is the only section that shows labels instead of section title
            if(section.title === 'CUSTOMIZE YOUR RACK' || section.selectors){
              (section.selectors || []).forEach(sel => {
                const key = selectorKey(sel);
                if(!state.selections[key]) missing.push(sel.label || key);
              });
            }
            
            // For other sections (PULL-UP BAR, BACK CROSS PIECE, etc.), show SECTION TITLE (not product names)
            // Only sections with products defined will be checked
            if(section.title && section.title !== 'CUSTOMIZE YOUR RACK'){
              const visibleProducts = (section.products || []).filter(p => conditionMatches(p));
              const hasProducts = visibleProducts.length > 0;
              
              // If section has products defined and none are selected, add the section title to missing list
              // This will show "PULL-UP BAR", "BACK CROSS PIECE", etc. in the hint
              if(hasProducts && !hasSectionProductSelected(section)){
                missing.push(section.title); // Use section title, not product title
              }
            }
          });
        } else {
          // Fallback to old structure (no sections)
          const selectors = step.selectors || [];
          const products = step.products || [];
          
          selectors.forEach(sel => {
            const key = selectorKey(sel);
            if(!state.selections[key]) missing.push(sel.label || key);
          });
          
          products.forEach(p => {
            // Skip products that auto-bind
            if(p.bind && p.bind.length > 0) return;
            if(p.selection==='single' && !state.lineItems[p.handle]){
              const prod = productsByHandle[p.handle];
              if(prod) missing.push(prod.title);
            }
          });
        }
      }
      return missing;
    }
    
    // Update Next button state
    function updateNextButtonState(){
      const isValid = validateStep(state.stepIndex);
      elNext.disabled = !isValid;
      updateValidationHint();
    }
    
    // Update validation hint message dynamically
    function updateValidationHint(){
      if(!elHint) return; // Safety check
      
      const step = steps[state.stepIndex];
      if(!step || !step.required){
        elHint.style.display = 'none';
        return;
      }
      
      const isValid = validateStep(state.stepIndex);
      if(!isValid){
        const missing = getMissingRequirements();
        if(missing.length > 0){
          const missingList = missing.join(' / ');
          elHint.textContent = `Please choose ${missingList} to continue`;
          elHint.style.display = 'block';
        } else {
          elHint.textContent = step.validation?.message || 'Please complete this step.';
          elHint.style.display = 'block';
        }
      } else {
        elHint.style.display = 'none';
      }
    }

    // Summary tab toggle
    elSummaryTab.addEventListener('click', ()=>{
      if(Object.keys(state.lineItems).length === 0) return; // Only clickable when there are items
      const isOpen = elSummaryTab.classList.contains('open');
      if(isOpen){
        elSummaryTab.classList.remove('open');
        elSummaryContent.style.display = 'none';
      } else {
        elSummaryTab.classList.add('open');
        elSummaryContent.style.display = 'block';
      }
    });
    
    // Reset button
    elReset.addEventListener('click', ()=>{
      if(confirm('Are you sure you want to reset all selections?')){
        state.lineItems = {};
        state.selections = {};
        state.selectionDetails = {};
        resolveBoundVariants();
        renderStep(state.stepIndex); // Re-render to reset all UI states
        refreshSummary(); // Update summary after re-render
        elSummaryTab.classList.remove('open');
        elSummaryContent.style.display = 'none';
      }
    });
    
    // Next button
    $('#js-next').addEventListener('click',()=>{ 
      if(!validateStep(state.stepIndex)){
        updateValidationHint();
        return;
      }
      if(state.stepIndex<steps.length-1){
        // Scroll to top of builder before rendering next step
        const builderWrap = document.querySelector('.builder-wrap');
        if(builderWrap) builderWrap.scrollIntoView({ behavior: 'instant', block: 'start' });
        renderStep(state.stepIndex+1);
      }
    });
    $('#js-back').addEventListener('click',()=>{ if(state.stepIndex>0) renderStep(state.stepIndex-1); });

    $('#js-add-to-cart').addEventListener('click', async ()=>{
      if(!validateAll()) return;
      syncBundledLineItems();
      const items=Object.values(state.lineItems).map(li=>{
        const props = {};
        Object.entries(state.selections || {}).forEach(([key, value]) => {
          if(!value) return;
          props[key] = state.selectionDetails?.[key]?.label || String(value);
        });
        if(li.bundleParentTitle) props._builder_required_for = li.bundleParentTitle;
        return { id: li.variantId, quantity: li.qty, properties: props };
      });
      try{
        const r=await fetch('/cart/add.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({items})});
        if(!r.ok) throw new Error('Add failed');
        
        // Trigger cart drawer open instead of redirecting
        document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
        // Also try direct cart drawer open
        if(window.theme && window.theme.CartDrawer){
          // Get cart drawer instance and open it
          const cartDrawerEl = document.querySelector('#CartDrawer');
          if(cartDrawerEl){
            // Trigger cart update event
            document.dispatchEvent(new CustomEvent('cart:updated'));
            // Open drawer
            document.dispatchEvent(new CustomEvent('cart:open'));
          }
        }
      }catch(e){ 
        console.error('Failed to add to cart:', e);
        alert('Failed to add to cart. Please try again.'); 
      }
    });

    // init
    // Note: resolveBoundVariants() will only bind if all required selections are present,
    // so calling it on init with empty selections is safe (no premature binding)
    // state.selections.Color = (rules.defaultColor && rules.defaultColor()) || (rules.allColors?.()[0]?.handle);
    renderStep(0); resolveBoundVariants(); refreshSummary(); updateValidationHint();
    
    // Expose debug helpers only in development/design mode
    if (window.Shopify && window.Shopify.designMode) {
      window.__builderDebug = {
        state: state,
        productsByHandle: productsByHandle,
        rules: rules,
        resolveBoundVariants: resolveBoundVariants,
        refreshSummary: refreshSummary,
        findVariant: (handle, criteria) => {
          const prod = productsByHandle[handle];
          if (!prod) return null;
          return findVariant(prod, criteria);
        },
        diagnose: () => {
          // Diagnostic function available only in design mode
          // Use browser dev tools to inspect state, productsByHandle, and rules directly
        }
      };
    }
  });
})();
